import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { DOTTUYENSINH_API } from '@app/utils/apiUrl';
import { Post } from '@app/utils/httpProvider';
import { 
    messageError, 
    messageToast, 
    status200,
    objectLoaiTuyenSinh,
    loaiTuyenSinhValue,
    objectTrangThaiDotTuyenSinh,
    trangThaiDotTuyenSinhValue,
    doiTuongValue,
    objectDoiTuong 
} from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import { Button } from '@components';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import DoiTuongMienGiamFields, { stringifyDoiTuongMienGiam } from './DoiTuongMienGiamFields';

const schema = yup.object().shape({
    tenDot: yup.string().required(messageError.emptyMessage).max(255, 'Tên đợt không được vượt quá 255 ký tự'),
    namTuyenSinh: yup.number().required(messageError.emptyMessage).min(1, 'Năm tuyển sinh phải lớn hơn 0'),
    loaiTuyenSinh: yup.string().required(messageError.emptyMessage),
    trangThaiDotTuyenSinh: yup.string().required(messageError.emptyMessage),
    doiTuong: yup.string().required(messageError.emptyMessage),
    lePhi: yup.number().min(0, 'Lệ phí không được nhỏ hơn 0').nullable(),
    chiTieuQuan: yup.number().min(0, 'Chỉ tiêu quân không được nhỏ hơn 0').nullable(),
    chiTieuDan: yup.number().min(0, 'Chỉ tiêu dân không được nhỏ hơn 0').nullable(),
    ngayNhanHoSo: yup.date().nullable(),
    ngayKetThucNhanHoSo: yup.date()
        .nullable()
        .min(yup.ref('ngayNhanHoSo'), 'Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu'),
    doiTuongMienGiams: yup.array().when('coDoiTuongMienGiam', {
        is: true,
        then: yup.array().of(yup.object().shape({
            tenDoiTuong: yup.string().trim().required('Vui lòng nhập tên đối tượng').max(255, 'Tên đối tượng không được vượt quá 255 ký tự')
        })).min(1, 'Vui lòng thêm ít nhất một đối tượng')
    }),
});

const DotTuyenSinhAdd = () => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const addFunc = useMutation((value) =>
        Post(DOTTUYENSINH_API.ADD_ITEM, null, value, false)
    );

    const handleCreate = async (values) => {
        const doiTuong = values.doiTuong;
        const { coDoiTuongMienGiam, doiTuongMienGiams, ...requestValues } = values;
        const model = {
            ...requestValues,
            chiTieuQuan: (doiTuong === doiTuongValue.quan || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuQuan === '' ? 0 : values.chiTieuQuan) : 0,
            chiTieuDan: (doiTuong === doiTuongValue.khac || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuDan === '' ? 0 : values.chiTieuDan) : 0,
            lePhi: values.lePhi === '' ? 0 : values.lePhi,
            ngayNhanHoSo: values.ngayNhanHoSo || null,
            ngayKetThucNhanHoSo: values.ngayKetThucNhanHoSo || null,
            doiTuongMienGiam: coDoiTuongMienGiam
                ? stringifyDoiTuongMienGiam(doiTuongMienGiams)
                : '',
        };

        addFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastCreateError);
                } else {
                    setIsLoading(true);
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.push(URI_INTERNAL.DOTTUYENSINH_PATH);
                }
            },
            onError: (res) => {
                toast.error(
                    res.message || 'Đã có lỗi xảy ra, vui lòng thử lại !'
                );
            }
        });
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới đợt tuyển sinh</Card.Header>
            <Card.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        tenDot: '',
                        namTuyenSinh: new Date().getFullYear(),
                        loaiTuyenSinh: loaiTuyenSinhValue.tuyenSinhTienSi,
                        trangThaiDotTuyenSinh: trangThaiDotTuyenSinhValue.sapMo,
                        doiTuong: doiTuongValue.quanVaDan,
                        chiTieuQuan: '',
                        chiTieuDan: '',
                        lePhi: 0,
                        ngayNhanHoSo: '',
                        ngayKetThucNhanHoSo: '',
                        coDoiTuongMienGiam: false,
                        doiTuongMienGiams: []
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên đợt',
                                        name: 'tenDot',
                                        component: (
                                            <>
                                                <Form.Control type="text" name="tenDot" value={values.tenDot} onChange={handleChange} />
                                                {errors.tenDot && <div className="text-danger small mt-1">{errors.tenDot}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Năm tuyển sinh',
                                        name: 'namTuyenSinh',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="namTuyenSinh" value={values.namTuyenSinh} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.namTuyenSinh && <div className="text-danger small mt-1">{errors.namTuyenSinh}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Loại tuyển sinh',
                                        name: 'loaiTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="loaiTuyenSinh"
                                                value={values.loaiTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectLoaiTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.loaiTuyenSinh
                                    },
                                    {
                                        label: 'Đối tượng',
                                        name: 'doiTuong',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="doiTuong"
                                                value={values.doiTuong}
                                                onChange={handleChange}
                                            >
                                                {objectDoiTuong.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.doiTuong
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    (values.doiTuong === doiTuongValue.quan || values.doiTuong === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu quân',
                                        name: 'chiTieuQuan',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="chiTieuQuan" value={values.chiTieuQuan} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.chiTieuQuan && <div className="text-danger small mt-1">{errors.chiTieuQuan}</div>}
                                            </>
                                        )
                                    } : null,
                                    (values.doiTuong === doiTuongValue.khac || values.doiTuong === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu dân',
                                        name: 'chiTieuDan',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="chiTieuDan" value={values.chiTieuDan} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.chiTieuDan && <div className="text-danger small mt-1">{errors.chiTieuDan}</div>}
                                            </>
                                        )
                                    } : null,
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Lệ phí',
                                        name: 'lePhi',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="lePhi" value={values.lePhi} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.lePhi && <div className="text-danger small mt-1">{errors.lePhi}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Trạng thái',
                                        name: 'trangThaiDotTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="trangThaiDotTuyenSinh"
                                                value={values.trangThaiDotTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectTrangThaiDotTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.trangThaiDotTuyenSinh
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày nhận hồ sơ',
                                        name: 'ngayNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayNhanHoSo" value={values.ngayNhanHoSo} onChange={handleChange} />
                                                {errors.ngayNhanHoSo && <div className="text-danger small mt-1">{errors.ngayNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Ngày kết thúc nhận',
                                        name: 'ngayKetThucNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKetThucNhanHoSo" value={values.ngayKetThucNhanHoSo} onChange={handleChange} />
                                                {errors.ngayKetThucNhanHoSo && <div className="text-danger small mt-1">{errors.ngayKetThucNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <DoiTuongMienGiamFields values={values} errors={errors} setFieldValue={setFieldValue} />
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={isLoading}>
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.DOTTUYENSINH_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                {isLoading && <div>Đang lưu...</div>}
            </Card.Body>
        </Card>
    );
};

export default DotTuyenSinhAdd;
